import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faPlus, 
  faUserShield, 
  faEye, 
  faEyeSlash, 
  faSpinner,
  faTrash,
  faEdit,
  faCheck,
  faTimes,
  faToggleOn,
  faToggleOff,
  faExclamationTriangle,
  faUserCog,
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import config from '../config/config';
import './AdminManagementPage.css';

const AdminManagementPage = () => {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  
  // State management
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Edit/Delete modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editFormData, setEditFormData] = useState({
    role: '',
    enabled: true,
    reason: ''
  });
  const [deleteFormData, setDeleteFormData] = useState({
    reason: '',
    transferData: false,
    transferToAdminId: ''
  });
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    phone: '',
    role: 'ADMIN'
  });
  
  // Form validation
  const [formErrors, setFormErrors] = useState({});

  // Fetch all admins on component mount
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/admins`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Only SUPERADMIN can view admin list.');
        }
        throw new Error(`Failed to fetch admins. Status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setAdmins(result.data || []);
      } else {
        throw new Error(result.message || 'Failed to load admins');
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    // Phone validation
    if (!formData.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
      errors.phone = 'Phone number is invalid';
    }
    
    // Role validation
    if (!formData.role) {
      errors.role = 'Role is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. Only SUPERADMIN can register new admins.');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to register admin. Status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        // Reset form and close modal
        setFormData({
          email: '',
          password: '',
          phone: '',
          role: 'ADMIN'
        });
        setShowAddForm(false);
        
        // Refresh admin list
        await fetchAdmins();
        
        // Show success message (you could add a toast notification here)
        alert('Admin registered successfully!');
      } else {
        throw new Error(result.message || 'Failed to register admin');
      }
    } catch (err) {
      console.error('Error registering admin:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'SUPERADMIN':
        return '#e91e63';
      case 'ADMIN':
        return '#2196f3';
      case 'TICKET_VOLUNTEER':
        return '#4caf50';
      case 'SALES_VOLUNTEER':
        return '#ff9800';
      default:
        return '#666';
    }
  };

  // Handle edit admin
  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setEditFormData({
      role: admin.role,
      enabled: admin.enabled,
      reason: ''
    });
    setShowEditModal(true);
  };

  // Handle delete admin
  const handleDeleteAdmin = (admin) => {
    setSelectedAdmin(admin);
    setDeleteFormData({
      reason: '',
      transferData: false,
      transferToAdminId: ''
    });
    setShowDeleteModal(true);
  };

  // Update admin role
  const updateAdminRole = async (adminId, newRole, reason) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: adminId,
          role: newRole,
          reason: reason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update role. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        await fetchAdmins(); // Refresh the list
        return result;
      } else {
        throw new Error(result.message || 'Failed to update admin role');
      }
    } catch (err) {
      console.error('Error updating admin role:', err);
      throw err;
    }
  };

  // Update admin status
  const updateAdminStatus = async (adminId, enabled, reason) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: adminId,
          enabled: enabled,
          reason: reason
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update status. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        await fetchAdmins(); // Refresh the list
        return result;
      } else {
        throw new Error(result.message || 'Failed to update admin status');
      }
    } catch (err) {
      console.error('Error updating admin status:', err);
      throw err;
    }
  };

  // Delete admin
  const deleteAdmin = async (adminId, reason, transferData = false, transferToAdminId = null) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      const requestBody = {
        adminId: adminId,
        reason: reason
      };

      if (transferData && transferToAdminId) {
        requestBody.transferData = true;
        requestBody.transferToAdminId = transferToAdminId;
      }

      const response = await fetch(`${config.API_BASE_URL}/um/admin/v1/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete admin. Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'success') {
        await fetchAdmins(); // Refresh the list
        return result;
      } else {
        throw new Error(result.message || 'Failed to delete admin');
      }
    } catch (err) {
      console.error('Error deleting admin:', err);
      throw err;
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdmin) return;

    try {
      setSubmitting(true);
      setError(null);

      // Update role if changed
      if (editFormData.role !== selectedAdmin.role) {
        await updateAdminRole(selectedAdmin.id, editFormData.role, editFormData.reason || 'Role updated via admin panel');
      }

      // Update status if changed
      if (editFormData.enabled !== selectedAdmin.enabled) {
        await updateAdminStatus(selectedAdmin.id, editFormData.enabled, editFormData.reason || 'Status updated via admin panel');
      }

      setShowEditModal(false);
      setSelectedAdmin(null);
      alert('Admin updated successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete form submission
  const handleDeleteSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdmin) return;

    try {
      setSubmitting(true);
      setError(null);

      await deleteAdmin(
        selectedAdmin.id,
        deleteFormData.reason,
        deleteFormData.transferData,
        deleteFormData.transferToAdminId || null
      );

      setShowDeleteModal(false);
      setSelectedAdmin(null);
      alert('Admin deleted successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Quick toggle admin status
  const quickToggleStatus = async (admin) => {
    try {
      setError(null);
      await updateAdminStatus(
        admin.id, 
        !admin.enabled, 
        `Status ${admin.enabled ? 'disabled' : 'enabled'} via quick toggle`
      );
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-management-container">
      <div className="admin-management-header">
        <button onClick={handleBack} className="back-button">
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
        </button>
        
        <div className="header-content">
          <h2><FontAwesomeIcon icon={faUserShield} /> Admin Management</h2>
          <p>Manage admin accounts and permissions</p>
        </div>
        
        <button 
          className="add-admin-button"
          onClick={() => setShowAddForm(true)}
        >
          <FontAwesomeIcon icon={faPlus} /> Add New Admin
        </button>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading ? (
        <div className="loading-indicator">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>Loading admins...</span>
        </div>
      ) : (
        <div className="admins-table-container">
          <table className="admins-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Verification</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.length > 0 ? (
                admins.map((admin) => (
                  <tr key={admin.id} className={!admin.enabled ? 'disabled-row' : ''}>
                    <td>{admin.id}</td>
                    <td>{admin.email}</td>
                    <td>{admin.phone || 'N/A'}</td>
                    <td>
                      <span 
                        className="role-badge" 
                        style={{ backgroundColor: getRoleColor(admin.role) }}
                      >
                        {admin.role}
                      </span>
                    </td>
                    <td>
                      <div className="status-cell">
                        <span className={`status-badge ${admin.enabled ? 'enabled' : 'disabled'}`}>
                          <FontAwesomeIcon icon={admin.enabled ? faCheckCircle : faTimesCircle} />
                          {admin.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          className={`status-toggle ${admin.enabled ? 'enabled' : 'disabled'}`}
                          onClick={() => quickToggleStatus(admin)}
                          title={`${admin.enabled ? 'Disable' : 'Enable'} Admin`}
                        >
                          <FontAwesomeIcon icon={admin.enabled ? faToggleOn : faToggleOff} />
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="verification-cell">
                        <span className={`verification-badge ${admin.emailVerified ? 'verified' : 'unverified'}`}>
                          <FontAwesomeIcon icon={admin.emailVerified ? faCheckCircle : faTimesCircle} />
                          Email
                        </span>
                        <span className={`verification-badge ${admin.phoneVerified ? 'verified' : 'unverified'}`}>
                          <FontAwesomeIcon icon={admin.phoneVerified ? faCheckCircle : faTimesCircle} />
                          Phone
                        </span>
                      </div>
                    </td>
                    <td>{formatDate(admin.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-button edit-button" 
                          title="Edit Admin"
                          onClick={() => handleEditAdmin(admin)}
                        >
                          <FontAwesomeIcon icon={faUserCog} />
                        </button>
                        <button 
                          className="action-button delete-button" 
                          title="Delete Admin"
                          onClick={() => handleDeleteAdmin(admin)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="no-records">
                    No admin records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Admin Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Admin</h3>
              <button 
                className="close-button"
                onClick={() => setShowAddForm(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={formErrors.email ? 'error' : ''}
                  placeholder="Enter admin email"
                />
                {formErrors.email && <span className="error-text">{formErrors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={formErrors.password ? 'error' : ''}
                    placeholder="Enter secure password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {formErrors.password && <span className="error-text">{formErrors.password}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={formErrors.phone ? 'error' : ''}
                  placeholder="Enter phone number"
                />
                {formErrors.phone && <span className="error-text">{formErrors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="role">Role *</label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className={formErrors.role ? 'error' : ''}
                >
                  <option value="ADMIN">Admin</option>
                  <option value="SUPERADMIN">Super Admin</option>
                  <option value="TICKET_VOLUNTEER">Ticket Volunteer</option>
                  <option value="SALES_VOLUNTEER">Sales Volunteer</option>
                </select>
                {formErrors.role && <span className="error-text">{formErrors.role}</span>}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Registering...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      Register Admin
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin Modal */}
      {showEditModal && selectedAdmin && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Admin: {selectedAdmin.email}</h3>
              <button 
                className="close-button"
                onClick={() => setShowEditModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="admin-form">
              <div className="form-group">
                <label htmlFor="editRole">Role</label>
                <select
                  id="editRole"
                  name="role"
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({...editFormData, role: e.target.value})}
                >
                  <option value="ADMIN">Admin</option>
                  <option value="SUPERADMIN">Super Admin</option>
                  <option value="TICKET_VOLUNTEER">Ticket Volunteer</option>
                  <option value="SALES_VOLUNTEER">Sales Volunteer</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="editEnabled">Status</label>
                <select
                  id="editEnabled"
                  name="enabled"
                  value={editFormData.enabled}
                  onChange={(e) => setEditFormData({...editFormData, enabled: e.target.value === 'true'})}
                >
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="editReason">Reason for Change</label>
                <textarea
                  id="editReason"
                  name="reason"
                  value={editFormData.reason}
                  onChange={(e) => setEditFormData({...editFormData, reason: e.target.value})}
                  placeholder="Enter reason for this change..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheck} />
                      Update Admin
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Admin Modal */}
      {showDeleteModal && selectedAdmin && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                Delete Admin: {selectedAdmin.email}
              </h3>
              <button 
                className="close-button"
                onClick={() => setShowDeleteModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="delete-warning">
              <p><strong>Warning:</strong> This action cannot be undone. The admin account will be permanently deleted.</p>
              <div className="admin-details">
                <p><strong>Admin ID:</strong> {selectedAdmin.id}</p>
                <p><strong>Email:</strong> {selectedAdmin.email}</p>
                <p><strong>Role:</strong> {selectedAdmin.role}</p>
                <p><strong>Status:</strong> {selectedAdmin.enabled ? 'Enabled' : 'Disabled'}</p>
              </div>
            </div>
            
            <form onSubmit={handleDeleteSubmit} className="admin-form">
              <div className="form-group">
                <label htmlFor="deleteReason">Reason for Deletion *</label>
                <textarea
                  id="deleteReason"
                  name="reason"
                  value={deleteFormData.reason}
                  onChange={(e) => setDeleteFormData({...deleteFormData, reason: e.target.value})}
                  placeholder="Enter reason for deleting this admin..."
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={deleteFormData.transferData}
                    onChange={(e) => setDeleteFormData({...deleteFormData, transferData: e.target.checked})}
                  />
                  Transfer data to another admin
                </label>
              </div>

              {deleteFormData.transferData && (
                <div className="form-group">
                  <label htmlFor="transferToAdminId">Transfer Data To</label>
                  <select
                    id="transferToAdminId"
                    name="transferToAdminId"
                    value={deleteFormData.transferToAdminId}
                    onChange={(e) => setDeleteFormData({...deleteFormData, transferToAdminId: e.target.value})}
                    required={deleteFormData.transferData}
                  >
                    <option value="">Select Admin</option>
                    {admins
                      .filter(admin => admin.id !== selectedAdmin.id && admin.enabled)
                      .map(admin => (
                        <option key={admin.id} value={admin.id}>
                          {admin.email} ({admin.role})
                        </option>
                      ))
                    }
                  </select>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="delete-button-confirm"
                  disabled={submitting || !deleteFormData.reason}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faTrash} />
                      Delete Admin
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagementPage;
